import React, { useState, useEffect } from 'react';
import { fileUrl } from '../utils/fileUrl';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent } from './ui/card';
import { User, Car, Hash, Star, Phone } from 'lucide-react';

const DriverInfoCard = ({ driverId }) => {
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (driverId) {
      fetchDriver();
    }
  }, [driverId]);

  const fetchDriver = async () => {
    try {
      const response = await axios.get(`${API}/profile/driver/${driverId}`);
      setDriver(response.data);
    } catch (error) {
      console.error('Failed to load driver info');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${index < Math.floor(rating) ? 'fill-gold-400 text-gold-400' : 'text-gray-300'}`}
      />
    ));
  };

  if (loading) return <div className="text-sm text-gray-500">Loading driver info...</div>;
  if (!driver) return null;

  return (
    <Card className="bg-blue-50 border-blue-200">
      <CardContent className="pt-4">
        <h4 className="font-semibold text-blue-900 mb-3">Your Driver</h4>
        <div className="flex items-start gap-4">
          {driver.profile_photo_url ? (
            <img
              src={fileUrl(driver.profile_photo_url)}
              alt={driver.name}
              className="w-16 h-16 rounded-full object-cover border-2 border-blue-300"
              onError={(e) => {
                console.error('Driver photo failed to load:', e.target.src);
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-blue-200 flex items-center justify-center">
              <User className="w-8 h-8 text-blue-600" />
            </div>
          )}
          <div className="flex-1 space-y-2">
            <div>
              <div className="font-semibold text-blue-900">{driver.name}</div>
              <div className="text-sm text-blue-700">{driver.email}</div>
              {driver.average_rating > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  {renderStars(driver.average_rating)}
                  <span className="text-sm text-blue-700 ml-1">
                    {driver.average_rating.toFixed(1)} ({driver.total_ratings} {driver.total_ratings === 1 ? 'rating' : 'ratings'})
                  </span>
                </div>
              )}
            </div>
            {driver.phone && (
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <Phone className="w-4 h-4" />
                <span>{driver.phone}</span>
              </div>
            )}
            {driver.car_type && (
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <Car className="w-4 h-4" />
                <span>{driver.car_type}</span>
              </div>
            )}
            {driver.license_plate && (
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <Hash className="w-4 h-4" />
                <span className="font-mono font-semibold">{driver.license_plate}</span>
              </div>
            )}
            {driver.bio && (
              <div className="text-sm text-blue-700 mt-2 pt-2 border-t border-blue-200">
                <p>{driver.bio}</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DriverInfoCard;
